import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import SpecimenCard from '../../components/market-place/specimen-card/SpecimenCard';
import Loader from '../../components/loader/Loader';
import { DropDownSVG, SearchSVG } from '../../components/svgs';
import { BUTTON_CONSTANTS, MARKETPLACE_LISTING_CONSTANTS, PAGE_HEADER_CONSTANTS } from '../../constants/common';
import DeactivateListingModal from './my-listing/DeactivateListingModal';
import EditListingModal from './my-listing/EditListingModal';
import SaleHistory from './SaleHistory';
import PaymentReport from './PaymentReport';
import {
  StandardParamsToODataParams,
  StandardParams,
  ODataParams,
  PagedResponse,
} from '../../types/interfaces/apiParams.interfaces';
import { MarketPlaceSpecimenResponse } from '../../types/interfaces/marketPlace.interfaces';
import { Genders } from '../../types/enums';
import { showToast } from '../../services/toast.service';
import {
  getMarketplaceSpecimensOData,
  removeMarketplaceListing,
  updateMarketplaceListing,
} from '../../api/marketPlaceSpecimenApi';
import { toastMessages } from '../../constants/errorMessages';
import './myListing.scss';

const pageSize = 8;

const buildODataParams = (accountId: number, search?: string, offset = 0, top = pageSize): ODataParams => {
  const normalizedSearch = search?.trim().replaceAll("'", "''");
  const searchFilter =
    normalizedSearch && normalizedSearch.length > 0
      ? `(contains(Animal/Name,'${normalizedSearch}') or contains(Animal/Breed/Name,'${normalizedSearch}') or contains(Animal/Breed/Species/Name,'${normalizedSearch}'))`
      : undefined;
  const standard: StandardParams = {
    include: 'Animal.Breed.Species, Animal.Attachments, Accounts, MarketPlaceSpecimenTagMappings,embryoStraw',
    filter: searchFilter
      ? `IsDeleted eq false and AccountId eq ${accountId} and ${searchFilter}`
      : `IsDeleted eq false and AccountId eq ${accountId}`,
    offset,
    limit: top,
  };

  return StandardParamsToODataParams(standard);
};

const isFemaleSpecimen = (specimen: MarketPlaceSpecimenResponse): boolean => {
  const gender = specimen.animal?.gender ?? specimen.specimen?.animal?.gender;
  return String(gender ?? '').toLowerCase() === Genders.Female.toLowerCase();
};

const MyListing: FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const [specimens, setSpecimens] = useState<MarketPlaceSpecimenResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagingOffset, setPagingOffset] = useState<number>(0);
  const [pagingMax, setPagingMax] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [deactivateName, setDeactivateName] = useState<string>('');
  const [deactivateId, setDeactivateId] = useState<number | null>(null);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [editSpecimen, setEditSpecimen] = useState<MarketPlaceSpecimenResponse | null>(null);

  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const isFetching = useRef(false);

  const fetchMyListings = useCallback(
    async (offset = 0) => {
      if (accountId == null) return;
      if (isFetching.current && offset > 0) return;
      isFetching.current = true;
      setIsLoading(true);

      try {
        const odata = buildODataParams(accountId, search, offset);
        const res = await getMarketplaceSpecimensOData(odata);
        const pagedResponse: PagedResponse<MarketPlaceSpecimenResponse> = res.data as PagedResponse<MarketPlaceSpecimenResponse>;
        const newSpecimens = pagedResponse.result;

        if (offset === 0) {
          setSpecimens(newSpecimens);
        } else {
          setSpecimens(prev => [...prev, ...newSpecimens]);
        }

        setPagingMax(pagedResponse.total);
        setPagingOffset(Math.min(offset + pageSize, pagedResponse.total));
      } catch {
        showToast.error(toastMessages.FAILED_TO_FETCH_MARKETPLACE_SPECIMENS);
      } finally {
        isFetching.current = false;
        setIsLoading(false);
      }
    },
    [accountId, search],
  );

  useEffect(() => {
    setPagingOffset(0);
    fetchMyListings(0);
  }, [fetchMyListings]);

  const handleEdit = (specimen: MarketPlaceSpecimenResponse) => {
    setEditSpecimen(specimen);
    setIsEditOpen(true);
  };

  const handleEditConfirm = async (price: number, quantity: number) => {
    setIsEditOpen(false);
    if (editSpecimen) {
      try {
        await updateMarketplaceListing(editSpecimen.marketPlaceSpecimenId, {
          animalId: editSpecimen.animal?.animalId!,
          specimenId: editSpecimen.specimenId,
          unitPriceInCents: price,
          quantity: quantity,
        });
        setSpecimens(prev =>
          prev.map(s =>
            s.marketPlaceSpecimenId === editSpecimen.marketPlaceSpecimenId ? { ...s, unitPriceInCents: price, quantity } : s,
          ),
        );
        showToast.success(toastMessages.SPECIMEN_UPDATE_SUCCESS);
      } catch {
        showToast.error(toastMessages.SPECIMEN_UPDATE_FAIL);
      }
    }
  };

  const handleDeactivate = (id: number, name: string) => {
    setDeactivateName(name);
    setDeactivateId(id);
    setIsOpen(true);
  };

  const handleDeactivateConfirm = async () => {
    setIsOpen(false);
    if (deactivateId) {
      try {
        await removeMarketplaceListing(deactivateId, 'Deactivated by seller');
        await fetchMyListings();
        showToast.success(toastMessages.DEACTIVATE_LISTING_SUCCESS);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    }
  };

  return (
    <div className="my-listing-page">
      <div className="my-listing card">
        <div className="my-listing__header">
          <div className="my-listing__header-top">
            <h2>{PAGE_HEADER_CONSTANTS.MY_LISTING}</h2>
            <span className="my-listing__active-count">{pagingMax} active listings</span>
          </div>

          <ul className="my-listing__tabs my-listing__tabs--desktop">
            <li className={activeTab === 0 ? 'active' : ''} onClick={() => setActiveTab(0)}>
              <div className="active-listings">{MARKETPLACE_LISTING_CONSTANTS.ACTIVE_LISTINGS}</div>
            </li>
            <li className={activeTab === 1 ? 'active' : ''} onClick={() => setActiveTab(1)}>
              <div className="sale-history">{MARKETPLACE_LISTING_CONSTANTS.SALE_HISTORY}</div>
            </li>
            <li className={activeTab === 2 ? 'active' : ''} onClick={() => setActiveTab(2)}>
              <div className="payment-report">{MARKETPLACE_LISTING_CONSTANTS.PAYMENT_REPORT}</div>
            </li>
          </ul>
        </div>

        <div className="my-listing__mobile-nav">
          <div className={`my-listing__nav-item ${activeTab === 0 ? 'active' : ''}`} onClick={() => setActiveTab(0)}>
            <div className="nav-item__content">
              <div className="nav-item__title">{MARKETPLACE_LISTING_CONSTANTS.ACTIVE_LISTINGS}</div>
            </div>
            <div className="nav-item__arrow">
              <DropDownSVG reversed={activeTab === 0} right={activeTab !== 0} />
            </div>
          </div>

          <div className={`my-listing__nav-item ${activeTab === 1 ? 'active' : ''}`} onClick={() => setActiveTab(1)}>
            <div className="nav-item__content">
              <div className="nav-item__title">{MARKETPLACE_LISTING_CONSTANTS.SALE_HISTORY}</div>
            </div>
            <div className="nav-item__arrow">
              <DropDownSVG reversed={activeTab === 1} right={activeTab !== 1} />
            </div>
          </div>

          <div className={`my-listing__nav-item ${activeTab === 2 ? 'active' : ''}`} onClick={() => setActiveTab(2)}>
            <div className="nav-item__content">
              <div className="nav-item__title">{MARKETPLACE_LISTING_CONSTANTS.PAYMENT_REPORT}</div>
            </div>
            <div className="nav-item__arrow">
              <DropDownSVG reversed={activeTab === 2} right={activeTab !== 2} />
            </div>
          </div>
        </div>

        <div className="my-listing__body">
          {activeTab === 0 && (
            <>
              <div className="my-listing__controls">
                <div className="my-listing__search">
                  <input
                    type="search"
                    placeholder="Search by animal name, Species or breed"
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                  />
                  <span className="my-listing__search-icon" aria-hidden>
                    <SearchSVG />
                  </span>
                </div>
              </div>
              {!isLoading && specimens.length === 0 ? (
                <div className="my-listing__empty">No listings found.</div>
              ) : (
                <div className="my-listing__grid">
                  {specimens.map(specimen => (
                    <SpecimenCard
                      key={specimen.marketPlaceSpecimenId}
                      animalId={specimen.animal?.animalId}
                      name={`${
                        specimen && isFemaleSpecimen(specimen) ? specimen?.embryoStraw?.embryoStrawCode : specimen?.specimenId
                      } - ${specimen?.animal?.name}`}
                      attachments={specimen.animal?.attachments}
                      breed={specimen.animal?.breed?.name}
                      species={specimen.animal?.breed?.species?.name}
                      gender={specimen.animal?.gender}
                      quantity={specimen.quantity}
                      price={specimen.unitPriceInCents}
                      accountId={specimen.accountId}
                      breederName={specimen.account?.name}
                      marketplaceSpecimenTagIds={specimen.marketPlaceSpecimenTagMappings?.map(
                        marketPlaceSpecimenTagMapping => marketPlaceSpecimenTagMapping.marketPlaceSpecimenTagId,
                      )}
                      onEdit={() => handleEdit(specimen)}
                      onDeactivate={() =>
                        handleDeactivate(specimen.marketPlaceSpecimenId, specimen.animal?.name ?? 'this listing')
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab == 0 && isLoading && <Loader loaderSize="medium" appLoader />}
          {activeTab == 0 && pagingOffset < pagingMax && (
            <div className="my-listing__view-more">
              <button type="button" className="button blue" disabled={isLoading} onClick={() => fetchMyListings(pagingOffset)}>
                {BUTTON_CONSTANTS.VIEW_MORE}
              </button>
            </div>
          )}

          {activeTab === 1 && accountId != null && <SaleHistory accountId={accountId} />}
          {activeTab === 2 && accountId != null && <PaymentReport accountId={accountId} />}
        </div>
      </div>

      <EditListingModal
        isOpen={isEditOpen}
        specimen={editSpecimen}
        onClose={() => setIsEditOpen(false)}
        onConfirm={handleEditConfirm}
      />
      <DeactivateListingModal
        isOpen={isOpen}
        name={deactivateName}
        onClose={() => setIsOpen(false)}
        onConfirm={handleDeactivateConfirm}
      />
    </div>
  );
};

export default MyListing;
